import {api} from "@/shared/api";
import {Combobox} from "@/shared/ui/combobox";
import * as TimeVacationAccountInfo from "./timeVacationAccountInfo";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {DOMUtil} from "@/shared/utils/dom";
import {
	OvertimeRule,
	OvertimeRuleEntry,
	TimeAccountData,
	TimeAccountTableRow,
	User,
	WorkBalanceCorrectionType
} from "@/shared/timr";
import {
	formatDate,
	formatDuration,
	getISODate,
	parseDateInput,
	parseDuration,
	parseISODate,
	Temporal
} from "@/shared/utils/datefunctions";
import {TimeAccountEntryType, WorkingTimeTypeSubCategory} from "@/shared/timrTypes";

export interface CorrectWorkBalanceDialogConfig {
	usersWithInitializedTimeAccount: User[];
	defaultOvertimeRules: OvertimeRule[];
}

export class CorrectWorkBalanceDialog {
	// Important! the constant value must be the same as in TimeAccountController.java
	private static readonly OVERTIME_RULE_TIME_ACCOUNT_ID = -1;
	private static readonly OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID = -2;
	private static readonly OVERTIME_RULE_ALL_TRAVELLING_TIME_ACCOUNTS_ID = -3;
	private static readonly OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID = -4;
	private static readonly OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID = -5;

	private static readonly OPTION_DATA_PERCENTAGE = "percentage";

	static readonly EVENT_TIME_ACCOUNT_CORRECTED = "timeAccountCorrected";

	private dom: JQuery;
	private modal: BootstrapModal.Modal;
	private userCombobox: Combobox<User>;
	private overtimeRuleCombobox: Combobox<OvertimeRuleEntry>;

	private updateCorrectTimeAccountDataRequest: JQuery.jqXHR;
	private loadOvertimeRulesRequest: JQuery.jqXHR;

	private editMode = false;
	private editTimeAccountId: number | null = null;

	constructor(config: CorrectWorkBalanceDialogConfig) {
		this.initCorrectWorkBalanceDialog(config);
	}

	public showCorrectWorkBalanceDialog(user: User) {
		this.modal.toggleButton("correctButton", true);
		this.modal.toggleButton("saveChangesButton", false);
		this.userCombobox.setSelectedItem(user, true);
		this.modal.show();
		if (this.userCombobox.getSelectedItem()?.id) {
			this.loadOvertimeRules(this.userCombobox.getSelectedItem().id);
		}
		this.setCorrectRadioButton();
		this.prepareCorrectWorkBalanceDialog(true);
	}

	public showForEdit(timeAccount: TimeAccountTableRow): void {
		this.editMode = true;
		this.editTimeAccountId = timeAccount.id;

		this.userCombobox.setSelectedItemById(timeAccount.userId, false);
		this.userCombobox.setDisabled(true);
		this.dom.find("#correctWorkBalanceDate").val(formatDate(parseISODate(timeAccount.date)));

		let correctionType: WorkBalanceCorrectionType;
		if (this.isPayoffEntryType(timeAccount.timeAccountEntryType)) {
			correctionType = "PAYOFF";
		} else if (this.isTransferEntryType(timeAccount.timeAccountEntryType)) {
			correctionType = "TRANSFER";
		} else {
			correctionType = "CORRECTION";
		}
		this.dom.find(`input[name=type][value=${correctionType}]`).prop("checked", true);

		this.loadOvertimeRules(timeAccount.userId).then(() => {
			this.setOvertimeRuleForEdit(timeAccount);
			this.overtimeRuleCombobox?.setDisabled(true);
			this.setCorrectRadioButton();
			this.dom.find("input[name=type]").prop("disabled", true);
			this.onCorrectionTypeChanged();
			this.prefillCorrectionValues(timeAccount);
			this.dom.find("#correctWorkBalanceDescription").val(timeAccount.description ?? "");
			this.modal.toggleButton("correctButton", false);
			this.modal.toggleButton("saveChangesButton", true);
			this.modal.show();
			// EVENT_CHANGED was suppressed during loadOvertimeRules → explicitly load balance info
			// (resetInputFields=false so prefilled values are not overwritten)
			this.prepareCorrectWorkBalanceDialog(false);
		});
	}

	public setUsersWithInitializedTimeAccount(usersWithInitializedTimeAccount: User[]) {
		this.userCombobox.updateItems(usersWithInitializedTimeAccount);
	}

	private initCorrectWorkBalanceDialog(config: CorrectWorkBalanceDialogConfig) {
		this.dom = $("#correctWorkBalanceDialog");
		this.modal = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.correctWorkBalance",
			message: this.dom,
			size: "modal-lg",
			buttons: [
				{
					text: "dialog.correct",
					action: () => this.correctWorkBalance(),
					css: "btn-primary correctButton"
				},
				{
					text: "dialog.saveChanges",
					action: () => this.editWorkBalanceCorrection(),
					css: "btn-primary saveChangesButton"
				},
				{
					text: "dialog.cancel"
				}
			],
			onClose: () => {
				this.dom.find("#correctWorkBalanceTypePayoff").prop("checked", true);
				this.onCorrectionTypeChanged();
				this.editMode = false;
				this.editTimeAccountId = null;
				this.userCombobox.setDisabled(false);
				this.dom.find("input[name=type]").prop("disabled", false);
				this.overtimeRuleCombobox?.setDisabled(false);
			}
		});
		this.userCombobox = new Combobox<User>({
			items: config.usersWithInitializedTimeAccount,
			formatter: user => user.fullname,
			idElement: this.dom.find("#correctWorkBalanceUserId"),
			filterElement: this.dom.find("#correctWorkBalanceUserFilter"),
			caretElement: this.dom.find("#correctWorkBalanceUserCaret"),
			clearable: false
		});
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => {
			// EVENT_CHANGED also fires when updateItems() clears the selection programmatically
			const user = this.userCombobox.getSelectedItem();
			if (!user) {
				return;
			}
			this.loadOvertimeRules(user.id);
			this.prepareCorrectWorkBalanceDialog(true);
		});
		this.dom.find("#correctWorkBalanceDate")
			.val(formatDate(Temporal.Now.plainDateISO()))
			.on("change", () => this.prepareCorrectWorkBalanceDialog(false));
		if (this.dom.find("#correctWorkBalanceOvertimeRuleId").length > 0) {
			this.overtimeRuleCombobox = new Combobox<OvertimeRuleEntry>({
				items: config.defaultOvertimeRules,
				// The categories for OVERTIME_RULE_TIME_ACCOUNT_ID and OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID doesn't have a shortName
				formatter: overtimeRule => this.getOvertimeRuleNameWithPercentage(overtimeRule),
				idElement: this.dom.find("#correctWorkBalanceOvertimeRuleId"),
				filterElement: this.dom.find("#correctWorkBalanceOvertimeRuleFilter"),
				caretElement: this.dom.find("#correctWorkBalanceOvertimeRuleCaret"),
				clearable: false
			});
			$(this.overtimeRuleCombobox).on(Combobox.EVENT_CHANGED, () => {
				// In edit mode the combobox is set up programmatically by setOvertimeRuleForEdit()
				// after updateItems fires this event. Running setCorrectRadioButton() here would
				// flip the type radio (CORRECTION→PAYOFF) while the combobox still holds the
				// previous session's selection (e.g. "All overtime accounts"). The explicit
				// prepareCorrectWorkBalanceDialog(false) call at the end of showForEdit's .then()
				// handles balance-info loading instead.
				if (this.editMode) {
					return;
				}
				this.setCorrectRadioButton();
				this.prepareCorrectWorkBalanceDialog(true);
			});
		}

		this.initCorrectWorkBalanceButtonListener();
		this.initCorrectionTypeBalanceListener();
	}

	private getOvertimeRuleNameWithPercentage(overtimeRule: OvertimeRuleEntry): string {
		const constantOvertimeRuleIds = [
			CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID,
			CorrectWorkBalanceDialog.OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID,
			CorrectWorkBalanceDialog.OVERTIME_RULE_ALL_TRAVELLING_TIME_ACCOUNTS_ID,
			CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID,
			CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID
		];
		const name = overtimeRule.shortName ? `${overtimeRule.shortName}: ${overtimeRule.name}` : overtimeRule.name;
		if (constantOvertimeRuleIds.includes(overtimeRule.id)) {
			return name;
		} else {
			return `${name} (${overtimeRule.percentage * 100}%)`;
		}
	}

	private setCorrectRadioButton(disabled: boolean = false) {
		const type: WorkBalanceCorrectionType = this.getSelectedCorrectionType();
		const isTimeAccountSelected = this.isTimeAccountSelected();
		const isAllTimeAccountSelected = this.isAllOvertimesAccountsSelected();
		const isAnyTravellingTimeAccountSelected = this.isAnyTravellingTimeAccountSelected();
		const radio = this.dom.find("input[name='type']");
		this.dom.find(".panel-timeAccount").toggle(isTimeAccountSelected && !isAnyTravellingTimeAccountSelected);
		this.dom.find(".panel-overtimeAllowance").toggle(!isTimeAccountSelected && !isAnyTravellingTimeAccountSelected);
		this.dom.find(".panel-travellingTime").toggle(isAnyTravellingTimeAccountSelected);
		if (isAllTimeAccountSelected && type === "CORRECTION") {
			radio.filter("#correctWorkBalanceTypePayoff").prop("checked", true);
		}
		radio.filter("#correctWorkBalanceTypeCorrection").prop("disabled", isAllTimeAccountSelected || disabled);
	}

	private initCorrectWorkBalanceButtonListener() {
		$(".paidOutValueButton").click(e => {
			const button = $(e.target).closest("button");
			let duration: number = button.data("balanceValue") as number;
			duration = this.getSelectedCorrectionType() === "CORRECTION" ? -duration : duration;
			if (button.hasClass("paidOutValueButton-timeAccount")) {
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), duration);
			} else if (button.hasClass("paidOutValueButton-overtime")) {
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), duration);
			} else if (button.hasClass("paidOutValueButton-allowance")) {
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), duration);
			} else if (button.hasClass("paidOutValueButton-travellingTime")) {
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue"), duration);
			}
			this.updateDescriptionText();
		});
	}

	private initCorrectionTypeBalanceListener() {
		this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId").change(() => this.updateDescriptionText());
		this.dom.find("input[name=type]").change(() => this.onCorrectionTypeChanged());
		this.dom.find(".correctionValue").change(e => {
			const input = $(e.target);
			this.setCorrectWorkBalanceCorrectionValue(input, parseDuration(input.val() as string, true) * 60000);
		});
	}

	private prepareCorrectWorkBalanceDialog(resetInputFields: boolean) {
		// Hide time account/overtime data if no user with initialized time account available
		if (!this.userCombobox.getSelectedItem()) {
			this.dom.find("#correctWorkBalanceTimeAccountInfoPanel").hide();
			return;
		}
		this.dom.find("#correctWorkBalanceTimeAccountInfoPanel").show();

		if (resetInputFields && !this.editMode) {
			this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue").val(formatDuration(0));
			this.dom.find("#correctWorkBalanceOvertimeCorrectionValue").val(formatDuration(0));
			this.dom.find("#correctWorkBalanceAllowanceCorrectionValue").val(formatDuration(0));
			this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue").val(formatDuration(0));
		}
		const user = this.userCombobox.getSelectedItem();
		const date = parseDateInput(this.dom.find("#correctWorkBalanceDate").val() as string);
		const loadingIndicator = this.dom.find("#correctWorkBalanceTimeAccountInfoPanel").find(".panel-processing");
		if (user?.id) {
			if (date) {
				loadingIndicator.toggle(true);
				this.loadCurrentTimeAccountData(user.id, date, (timeAccount: TimeAccountData) => {
					const isAllOvertimesAccountsSelected = this.isAllOvertimesAccountsSelected();
					const isAllTravellingTimeAccountsSelected = this.isAllTravellingTimeAccountsSelected();
					/* Hide panel with current time account data if "all overtimes/allowances" is selected
					 * Apply the total (received) value to the new total balance field and disable the field
					 * (currently it's only possible to make a transfer/correction with the total value)
					 */
					this.dom.find("#correctWorkBalanceTimeAccountInfoPanel").toggle(!isAllOvertimesAccountsSelected && !isAllTravellingTimeAccountsSelected);
					this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue").prop("disabled", isAllOvertimesAccountsSelected);
					this.dom.find("#correctWorkBalanceOvertimeCorrectionValue").prop("disabled", isAllOvertimesAccountsSelected);
					this.dom.find("#correctWorkBalanceAllowanceCorrectionValue").prop("disabled", isAllOvertimesAccountsSelected);
					this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue").prop("disabled", isAllTravellingTimeAccountsSelected);
					if (isAllOvertimesAccountsSelected) {
						loadingIndicator.toggle(false);
						if (!this.editMode) {
							this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), timeAccount.workDurationCurrentTotalBalance);
							this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), timeAccount.overtimeDurationCurrentTotalBalance);
							this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), timeAccount.allowanceDurationCurrentTotalBalance);
							this.updateDescriptionText();
						}
						return;
					}
					if (isAllTravellingTimeAccountsSelected) {
						loadingIndicator.toggle(false);
						if (!this.editMode) {
							this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), timeAccount.workDurationCurrentTotalBalance);
							this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue"), timeAccount.overtimeDurationCurrentTotalBalance);
							this.updateDescriptionText();
						}
						return;
					}
					TimeVacationAccountInfo.setDurationFieldValues(this.modal.getDomModal(), timeAccount);
					this.setTimeAccountButtonData(timeAccount);
					this.toggleValueCorrectionButtons(timeAccount);
					if (!this.editMode) {
						this.updateDescriptionText();
					}
					loadingIndicator.toggle(false);
				});
			}
		}
	}

	private loadCurrentTimeAccountData(userId: number, date: Temporal.PlainDate, success: (timeAccountData: TimeAccountData) => void) {
		this.updateCorrectTimeAccountDataRequest?.abort();
		this.updateCorrectTimeAccountDataRequest = api("timeaccounts/getCorrectTimeAccountData.json")
			.get({
				userId: userId,
				date: getISODate(date),
				overtimeRuleId: this.overtimeRuleCombobox.getSelectedItem()?.id,
				percentage: this.overtimeRuleCombobox.getSelectedItem()?.percentage
			})
			.done(success);
	}

	private correctWorkBalance() {
		const selectedUser = this.userCombobox.getSelectedItem();
		if (selectedUser) {
			const selectedOvertimeRule = this.overtimeRuleCombobox?.getSelectedItem();
			const timeAccountTransferReceiverIdSelect = this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId");
			void api("timeaccounts/correctWorkBalance.json")
				.post(this.getParams(selectedUser, selectedOvertimeRule, timeAccountTransferReceiverIdSelect))
				.done(() => {
					$(this).triggerHandler(CorrectWorkBalanceDialog.EVENT_TIME_ACCOUNT_CORRECTED);
					this.modal.close();
				})
				.always(() => this.modal.resetLoadingButtons());
		} else {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.noUsersInitialized.title",
				message: "report.timeAccountDashboard.noUsersInitialized.message"
			});
		}
	}

	private editWorkBalanceCorrection(): void {
		const selectedUser = this.userCombobox.getSelectedItem();
		const selectedOvertimeRule = this.overtimeRuleCombobox?.getSelectedItem();
		if (selectedUser && this.editTimeAccountId != null) {
			const timeAccountTransferReceiverIdSelect = this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId");
			void api("timeaccounts/editWorkBalanceCorrection.json")
				.post({
					timeAccountId: this.editTimeAccountId,
					...this.getParams(selectedUser, selectedOvertimeRule, timeAccountTransferReceiverIdSelect)
				}).done(() => {
					$(this).triggerHandler(CorrectWorkBalanceDialog.EVENT_TIME_ACCOUNT_CORRECTED);
					this.modal.close();
				}).always(() => this.modal.resetLoadingButtons());
		}
	}

	private getParams(selectedUser: User, selectedOvertimeRule: OvertimeRuleEntry, timeAccountTransferReceiverIdSelect: JQuery<HTMLElement>) {
		return {
			userId: selectedUser.id,
			date: getISODate(parseDateInput(this.dom.find("#correctWorkBalanceDate").val() as string)),
			timeAccountCorrectionValue: parseDuration(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue").val() as string, true) * 60000,
			overtimeCorrectionValue: parseDuration(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue").val() as string, true) * 60000,
			allowanceCorrectionValue: parseDuration(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue").val() as string, true) * 60000,
			travellingTimeCorrectionValue: parseDuration(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue").val() as string, true) * 60000,
			description: this.dom.find("#correctWorkBalanceDescription").val() as string,
			overtimeRuleId: selectedOvertimeRule?.id,
			secondaryOvertimeRuleId: timeAccountTransferReceiverIdSelect.val() as number,
			percentage: this.overtimeRuleCombobox.getSelectedItem()?.percentage,
			secondaryPercentage: timeAccountTransferReceiverIdSelect.find("option:selected").data(CorrectWorkBalanceDialog.OPTION_DATA_PERCENTAGE) as number,
			type: this.dom.find("input[name=type]:checked").val() as WorkBalanceCorrectionType
		};
	}

	/**
	 * Fetches overtime rules, and applies them to the combobox.
	 * Also contains This will also return 5 items that are not true overtime rules, identifiable by negative IDs
	 * OVERTIME_RULE_TIME_ACCOUNT_ID = -1
	 * OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID = -2
	 * OVERTIME_RULE_ALL_TRAVELLING_TIME_ACCOUNTS_ID = -3
	 * OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID = -4
	 * OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID = -5
	 * @param userId
	 * @private
	 */
	private loadOvertimeRules(userId: number): PromiseLike<void> {
		if (this.overtimeRuleCombobox) {
			this.loadOvertimeRulesRequest?.abort();
			this.loadOvertimeRulesRequest = api("timeaccounts/getOvertimeRules.json")
				.get({
					userId: userId
				})
				.done(data => {
					const selectedItem = this.overtimeRuleCombobox.getSelectedItem();
					this.overtimeRuleCombobox.updateItems(data.overtimeRules, selectedItem, true);
					// Remove transfer button if no rules are available (Stub time account rule "id = -1" is always available)
					this.dom.find("#correctWorkBalanceTypeTransfer").closest(".radio-inline").toggle(data.overtimeRules.length > 1);
				});
			return this.loadOvertimeRulesRequest;
		}
		return Promise.resolve();
	}

	/**
	 * Toggles the value correction buttons, which are visualized with an arrow down in the UI.
	 * The buttons are only visible when the values are positive or negative when the correction type is selected.
	 * @param timeAccountData is transferred when an item of the overtimeRuleCombobox is selected, if only the correction type is changed it is null
	 * @private
	 */
	private toggleValueCorrectionButtons(timeAccountData?: TimeAccountData) {
		const correction = this.getSelectedCorrectionType() === "CORRECTION";
		this.toggleCorrectionButton(this.dom.find("#workDurationLastPeriodPaymentDateButton"), correction, timeAccountData?.lastWorkDurationTotalBalance);
		this.toggleCorrectionButton(this.dom.find("#workDurationCurrentPeriodPaymentDateButton"), correction, timeAccountData?.workDurationCurrentBalance);
		this.toggleCorrectionButton(this.dom.find("#workDurationTotalPaymentDateButton"), correction, timeAccountData?.workDurationCurrentTotalBalance);
		this.toggleCorrectionButton(this.dom.find("#overtimeDurationLastPeriodPaymentDateButton"), correction, timeAccountData?.lastOvertimeDurationTotalBalance);
		this.toggleCorrectionButton(this.dom.find("#overtimeDurationCurrentPeriodPaymentDateButton"), correction, timeAccountData?.overtimeDurationCurrentBalance);
		this.toggleCorrectionButton(this.dom.find("#overtimeDurationTotalPaymentDateButton"), correction, timeAccountData?.overtimeDurationCurrentTotalBalance);
		this.toggleCorrectionButton(this.dom.find("#allowanceDurationLastPeriodPaymentDateButton"), correction, timeAccountData?.lastAllowanceDurationTotalBalance);
		this.toggleCorrectionButton(this.dom.find("#allowanceDurationCurrentPeriodPaymentDateButton"), correction, timeAccountData?.allowanceDurationCurrentBalance);
		this.toggleCorrectionButton(this.dom.find("#allowanceDurationTotalPaymentDateButton"), correction, timeAccountData?.allowanceDurationCurrentTotalBalance);
	}

	/**
	 * Toggles a single value correction button. If only the correction type has been changed, value is null.
	 * In this case, the data stored in the button ("balanceValue") will be used.
	 * @param button
	 * @param correctionTypeSelected
	 * @param value is transferred when an item of the overtimeRuleCombobox is selected, if only the correction type is changed it is null
	 * @private
	 */
	private toggleCorrectionButton(button: JQuery, correctionTypeSelected: boolean, value?: number) {
		value = value ?? button.data("balanceValue") as number;
		button.toggle((correctionTypeSelected && value < 0) || value > 0);
	}

	/**
	 * Stores the data to use it for the updateDescriptionText() method, which applies the value to the balance value and description
	 * @param timeAccountData
	 * @private
	 */
	private setTimeAccountButtonData(timeAccountData: TimeAccountData) {
		this.dom.find("#workDurationLastPeriodPaymentDateButton").data("balanceValue", timeAccountData.lastWorkDurationTotalBalance);
		this.dom.find("#workDurationCurrentPeriodPaymentDateButton").data("balanceValue", timeAccountData.workDurationCurrentBalance);
		this.dom.find("#workDurationTotalPaymentDateButton").data("balanceValue", timeAccountData.workDurationCurrentTotalBalance);
		this.dom.find("#overtimeDurationLastPeriodPaymentDateButton").data("balanceValue", timeAccountData.lastOvertimeDurationTotalBalance);
		this.dom.find("#overtimeDurationCurrentPeriodPaymentDateButton").data("balanceValue", timeAccountData.overtimeDurationCurrentBalance);
		this.dom.find("#overtimeDurationTotalPaymentDateButton").data("balanceValue", timeAccountData.overtimeDurationCurrentTotalBalance);
		this.dom.find("#allowanceDurationLastPeriodPaymentDateButton").data("balanceValue", timeAccountData.lastAllowanceDurationTotalBalance);
		this.dom.find("#allowanceDurationCurrentPeriodPaymentDateButton").data("balanceValue", timeAccountData.allowanceDurationCurrentBalance);
		this.dom.find("#allowanceDurationTotalPaymentDateButton").data("balanceValue", timeAccountData.allowanceDurationCurrentTotalBalance);
		this.dom.find("#travellingTimeLastPeriodPaymentDateButton").data("balanceValue", timeAccountData.lastOvertimeDurationTotalBalance);
		this.dom.find("#travellingTimeCurrentPeriodPaymentDateButton").data("balanceValue", timeAccountData.overtimeDurationCurrentBalance);
		this.dom.find("#travellingTimeTotalPaymentDateButton").data("balanceValue", timeAccountData.overtimeDurationCurrentTotalBalance);
	}

	/**
	 * Applies the value of to the balance input field and a text to the description note field
	 * The description text depends on the selected type, date, overtimeRule/account and balance value
	 */
	private updateDescriptionText() {
		const type: WorkBalanceCorrectionType = this.getSelectedCorrectionType();
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		const timeAccountDuration = parseDuration(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue").val() as string, true) * 60000;
		const overtimeDuration = parseDuration(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue").val() as string, true) * 60000;
		const allowanceDuration = parseDuration(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue").val() as string, true) * 60000;
		const travellingTimeDuration = parseDuration(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue").val() as string, true) * 60000;

		let descriptionMessageKey: string;
		let args: string[];
		const ruleName = overtimeRule ? this.getOvertimeRuleNameWithPercentage(overtimeRule) : "";
		if (this.isAllOvertimesAccountsSelected()) {
			descriptionMessageKey = ".ALL";
			args = [formatDuration(overtimeDuration), formatDuration(allowanceDuration)];
		} else if (this.isTimeAccountSelected()) {
			const selectedOption = this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId option:selected");
			const selectedOvertimeId = this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId").val() as number;
			const selectedPercentage = selectedOption.data(CorrectWorkBalanceDialog.OPTION_DATA_PERCENTAGE) as number;
			descriptionMessageKey = type === "TRANSFER" && selectedOvertimeId > 0 ? ".TIME_ACCOUNT.allowance" : ".TIME_ACCOUNT";
			args = [ruleName, formatDuration(timeAccountDuration), formatDuration(timeAccountDuration * selectedPercentage), selectedOption.text()];
		} else if (overtimeDuration && !allowanceDuration) {
			descriptionMessageKey = ".OVERTIME";
			args = [ruleName, formatDuration(overtimeDuration)];
		} else if (allowanceDuration && !overtimeDuration) {
			descriptionMessageKey = ".ALLOWANCE";
			args = [ruleName, formatDuration(allowanceDuration)];
		} else if (overtimeDuration && allowanceDuration || this.isAnyOvertimeTimeAccountSelected()) {
			descriptionMessageKey = ".OVERTIME_ALLOWANCE";
			args = [ruleName, formatDuration(overtimeDuration), formatDuration(allowanceDuration)];
		} else if (this.isAllTravellingTimeAccountsSelected()) {
			descriptionMessageKey = ".TRAVELLING_TIME.ALL";
			args = [formatDuration(travellingTimeDuration)];
		} else {
			descriptionMessageKey = ".TRAVELLING_TIME";
			args = [ruleName, formatDuration(travellingTimeDuration)];
		}

		if (type === "TRANSFER") {
			args.push(this.dom.find("#correctWorkBalanceOvertimeAllowanceTransferReceiverId").val() as string);
		}

		descriptionMessageKey = "report.timeAccountDashboard.balanceWorkDuration." + type + descriptionMessageKey;
		const descriptionText = messages.withArguments(descriptionMessageKey, ...args);
		this.dom.find("#correctWorkBalanceDescription").val(descriptionText);
		this.updateAllowCorrectionButton();
	}

	private onCorrectionTypeChanged() {
		const type: WorkBalanceCorrectionType = this.getSelectedCorrectionType();
		this.dom.find("label[for=correctWorkBalanceTimeAccountCorrectionValue]").text(messages["report.timeAccountDashboard.balance.new.timeAccount." + type]);
		this.dom.find("label[for=correctWorkBalanceOvertimeCorrectionValue]").text(messages["report.timeAccountDashboard.balance.new.overtime." + type]);
		this.dom.find("label[for=correctWorkBalanceAllowanceCorrectionValue]").text(messages["report.timeAccountDashboard.balance.new.allowance." + type]);
		this.dom.find(".correctTransferContainer").toggle(type === "TRANSFER");
		const timeAccountTransferReceiverSelect = this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId");
		DOMUtil.fillSelect(timeAccountTransferReceiverSelect,
			this.overtimeRuleCombobox.getItems(),
			false,
			(overtimeRule) => overtimeRule.id > 0 ||
			overtimeRule.id === CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID ||
			overtimeRule.id === CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID ?
				DOMUtil.createOption(String(overtimeRule.id), this.getOvertimeRuleNameWithPercentage(overtimeRule), {
					key: CorrectWorkBalanceDialog.OPTION_DATA_PERCENTAGE,
					value: overtimeRule.percentage
				}) : null
		);
		this.updateCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), type);
		this.updateCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), type);
		this.updateCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), type);
		this.toggleValueCorrectionButtons();
	}

	private updateCorrectWorkBalanceCorrectionValue(input: JQuery, type: WorkBalanceCorrectionType) {
		const duration: number = parseDuration(input.val() as string, true) * 60000;
		this.setCorrectWorkBalanceCorrectionValue(input, duration, type === "CORRECTION" && duration > 0);
		this.updateDescriptionText();
	}

	private setCorrectWorkBalanceCorrectionValue(input: JQuery, duration: number, prependMinus?: boolean) {
		Input.removeFeedback(input);
		const type: WorkBalanceCorrectionType = this.getSelectedCorrectionType();
		const formattedDuration = formatDuration(duration);
		if (type !== "CORRECTION" && duration < 0) {
			input.val("");
			const name = input.closest(".form-group").find("label").first().text();
			Input.addFeedback(input, "has-warning", messages.withArguments("report.timeAccountDashboard.balanceWorkDuration.negative.warning", name, formattedDuration));
		} else {
			input.val((prependMinus ? "-" : "") + formattedDuration);
		}
		this.updateDescriptionText();
	}

	/**
	 * Returns true if the selected item of the combobox is the time account (OVERTIME_RULE_TIME_ACCOUNT_ID) or empty
	 * @private
	 */
	private isAllOvertimesAccountsSelected(): boolean {
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		return !overtimeRule || overtimeRule.id === CorrectWorkBalanceDialog.OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID;
	}

	/**
	 * Returns true if the selected item of the combobox is the all travelling time accounts (OVERTIME_RULE_ALL_TRAVELLING_TIME_ID)
	 * @private
	 */
	private isAllTravellingTimeAccountsSelected(): boolean {
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		return overtimeRule?.id === CorrectWorkBalanceDialog.OVERTIME_RULE_ALL_TRAVELLING_TIME_ACCOUNTS_ID;
	}

	/**
	 * Returns true if the selected item of the combobox is any time account traveling time
	 * @private
	 */
	private isAnyTravellingTimeAccountSelected(): boolean {
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		return overtimeRule?.id === CorrectWorkBalanceDialog.OVERTIME_RULE_ALL_TRAVELLING_TIME_ACCOUNTS_ID ||
			overtimeRule?.id === CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID ||
			overtimeRule?.id === CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID;
	}

	/**
	 * Returns true if the selected item of the combobox is any time account overtime time
	 * @private
	 */
	private isAnyOvertimeTimeAccountSelected(): boolean {
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		return overtimeRule?.id >= 1;
	}

	/**
	 * Returns true if the selected item of the combobox is the time account (OVERTIME_RULE_TIME_ACCOUNT_ID) or empty
	 * @private
	 */
	private isTimeAccountSelected(): boolean {
		const overtimeRule = this.overtimeRuleCombobox.getSelectedItem();
		return !overtimeRule || overtimeRule.id === CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID;
	}

	private getSelectedCorrectionType(): WorkBalanceCorrectionType {
		return this.dom.find("input[name=type]:checked").val() as WorkBalanceCorrectionType;
	}

	private updateAllowCorrectionButton() {
		const enabled = !this.isTimeAccountSelected() || this.getSelectedCorrectionType() !== "TRANSFER" || !!this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId").val();
		Input.toggle(this.modal.getButtons("correctButton"), enabled);
		Input.toggle(this.modal.getButtons("saveChangesButton"), enabled);
	}

	private setOvertimeRuleForEdit(timeAccount: TimeAccountTableRow): void {
		if (!this.overtimeRuleCombobox) return;
		let overtimeRuleId: number;
		if ([TimeAccountEntryType.OvertimeCorrection, TimeAccountEntryType.OverTimePayoff].includes(timeAccount.timeAccountEntryType)) {
			overtimeRuleId = timeAccount.overtimes?.[0]?.overtimeRule?.id ?? CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID;
		} else if ([TimeAccountEntryType.TravellingTimeCorrection, TimeAccountEntryType.TravellingTimePayoff].includes(timeAccount.timeAccountEntryType)) {
			const subCategory = timeAccount.travellingTimes?.[0]?.subCategory;
			overtimeRuleId = subCategory === WorkingTimeTypeSubCategory.ActiveDrivingTime
				? CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID
				: CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID;
		} else if (timeAccount.timeAccountEntryType === TimeAccountEntryType.OvertimeTimeAccountTransfer) {
			// FROM overtime TO time account: workDurationBalance > 0, use the overtime rule as source
			// FROM time account TO overtime: workDurationBalance < 0, use TIME_ACCOUNT as source
			overtimeRuleId = (timeAccount.workDurationBalance ?? 0) > 0
				? (timeAccount.overtimes?.[0]?.overtimeRule?.id ?? CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID)
				: CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID;
		} else if (timeAccount.timeAccountEntryType === TimeAccountEntryType.TravellingTimeTransfer) {
			if ((timeAccount.workDurationBalance ?? 0) > 0) {
				// FROM travelling time TO time account: use the travelling time account as source
				const subCategory = timeAccount.travellingTimes?.[0]?.subCategory;
				overtimeRuleId = subCategory === WorkingTimeTypeSubCategory.ActiveDrivingTime
					? CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID
					: CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID;
			} else {
				// FROM time account TO travelling time: use TIME_ACCOUNT as source
				overtimeRuleId = CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID;
			}
		} else {
			overtimeRuleId = CorrectWorkBalanceDialog.OVERTIME_RULE_TIME_ACCOUNT_ID;
		}
		const rule = this.overtimeRuleCombobox.getItems().find(r => r.id === overtimeRuleId);
		if (rule) {
			this.overtimeRuleCombobox.setSelectedItem(rule, false, false, false);
		}
	}

	private prefillCorrectionValues(timeAccount: TimeAccountTableRow): void {
		switch (timeAccount.timeAccountEntryType) {
			case TimeAccountEntryType.WorkBalanceCorrection:
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), timeAccount.workDurationBalance ?? 0);
				break;
			case TimeAccountEntryType.Payoff:
				this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), Math.abs(timeAccount.workBalancePayoffs ?? 0));
				break;
			case TimeAccountEntryType.OvertimeCorrection: {
				const overtime = timeAccount.overtimes?.[0];
				if (overtime) {
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), overtime.overtimeCorrections ?? 0);
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), overtime.allowanceCorrections ?? 0);
				}
				break;
			}
			case TimeAccountEntryType.OverTimePayoff: {
				const overtime = timeAccount.overtimes?.[0];
				if (overtime) {
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), Math.abs(overtime.overtimePayoffs ?? 0));
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), Math.abs(overtime.allowancePayoffs ?? 0));
				}
				break;
			}
			case TimeAccountEntryType.TravellingTimeCorrection: {
				const travellingTime = timeAccount.travellingTimes?.[0];
				if (travellingTime) {
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue"), travellingTime.overtimeCorrections ?? 0);
				}
				break;
			}
			case TimeAccountEntryType.TravellingTimePayoff: {
				const travellingTime = timeAccount.travellingTimes?.[0];
				if (travellingTime) {
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue"), Math.abs(travellingTime.overtimePayoffs ?? 0));
				}
				break;
			}
			case TimeAccountEntryType.OvertimeTimeAccountTransfer: {
				if ((timeAccount.workDurationBalance ?? 0) > 0) {
					// FROM overtime TO time account
					const overtime = timeAccount.overtimes?.[0];
					if (overtime) {
						this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceOvertimeCorrectionValue"), Math.abs(overtime.overtimeTransfers ?? 0));
						this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceAllowanceCorrectionValue"), Math.abs(overtime.allowanceTransfers ?? 0));
					}
				} else {
					// FROM time account TO overtime
					const overtime = timeAccount.overtimes?.[0];
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), Math.abs(timeAccount.workDurationBalance ?? 0));
					if (overtime) {
						this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId")
							.val(String(overtime.overtimeRule?.id ?? ""));
					}
				}
				break;
			}
			case TimeAccountEntryType.TravellingTimeTransfer: {
				if ((timeAccount.workDurationBalance ?? 0) > 0) {
					// FROM travelling time TO time account
					const travellingTime = timeAccount.travellingTimes?.[0];
					if (travellingTime) {
						this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTravellingTimeCorrectionValue"), Math.abs(travellingTime.overtimeTransfers ?? 0));
					}
				} else {
					// FROM time account TO travelling time
					const travellingTime = timeAccount.travellingTimes?.[0];
					this.setCorrectWorkBalanceCorrectionValue(this.dom.find("#correctWorkBalanceTimeAccountCorrectionValue"), Math.abs(timeAccount.workDurationBalance ?? 0));
					if (travellingTime) {
						const targetId = travellingTime.subCategory === WorkingTimeTypeSubCategory.ActiveDrivingTime
							? CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_ACTIVE_ID
							: CorrectWorkBalanceDialog.OVERTIME_RULE_TRAVELLING_TIME_PASSIVE_ID;
						this.dom.find("#correctWorkBalanceTimeAccountTransferReceiverId").val(String(targetId));
					}
				}
				break;
			}
		}
	}

	private isPayoffEntryType(entryType: TimeAccountEntryType): boolean {
		return [TimeAccountEntryType.Payoff, TimeAccountEntryType.OverTimePayoff,
			TimeAccountEntryType.TravellingTimePayoff].includes(entryType);
	}

	private isTransferEntryType(entryType: TimeAccountEntryType): boolean {
		return [TimeAccountEntryType.OvertimeTimeAccountTransfer, TimeAccountEntryType.TravellingTimeTransfer].includes(entryType);
	}
}
